import { TeamMemberRole, TeamMemberStatus } from '__generated__/GQL';

import { FREE_ROLES } from './constants';
import { TeamMembers } from './hooks/useTeamMembers';
import { tabToFilter } from './pages/Members/types';
import { MembersTab } from './routes';

export const hasRole = (
  teamMember: { roles: TeamMemberRole[] | null },
  role: TeamMemberRole | null,
): boolean => {
  return role !== null && (teamMember.roles?.includes(role) ?? false);
};

export const isFreeRole = (role: TeamMemberRole) => {
  return FREE_ROLES.includes(role);
};

const boolToNumber = (b: boolean): 1 | 0 => (b ? 1 : 0);

export const computeRoleBillingDelta = (
  newRole: TeamMemberRole,
  currentRole?: TeamMemberRole,
): number => {
  if (currentRole) {
    const currentRoleWeight = boolToNumber(!isFreeRole(currentRole));
    const newRoleWeight = boolToNumber(!isFreeRole(newRole));
    return newRoleWeight - currentRoleWeight;
  }
  return 0;
};

export const isFreeMember = (teamMember: {
  roles: TeamMemberRole[] | null;
}): boolean => {
  if (!teamMember.roles) {
    return false;
  }
  // a free member has only roles that are marked as free
  return teamMember.roles.every(isFreeRole);
};

const ROLE_ORDER = {
  [TeamMemberRole.None]: -1,
  [TeamMemberRole.Accountant]: 0,
  [TeamMemberRole.Employee]: 1,
  [TeamMemberRole.Admin]: 2,
  [TeamMemberRole.AccountHolder]: 3,
};

export const getHighestRole = (
  roles: TeamMemberRole[],
): TeamMemberRole | undefined => {
  if (roles.length === 0) {
    return undefined;
  }

  return roles.reduce((highest, role) =>
    ROLE_ORDER[role] > ROLE_ORDER[highest] ? role : highest,
  );
};

export const computeFilteredMembers = ({
  isFreeRolesOnly,
  query,
  selectedTab,
  teamMembers,
}: {
  teamMembers: TeamMembers;
  selectedTab: MembersTab | null;
  query: string;
  isFreeRolesOnly: boolean;
}) => {
  const roleFilteredMembers = teamMembers.filter((member) => {
    if (selectedTab === 'locked') {
      return member.status === TeamMemberStatus.Locked;
    }
    // If there is no selected filter, we keep every member otherwise we keep only the ones with the selected role
    // We only want to display Locked user inside the corresponding category
    if (
      (selectedTab === null || selectedTab === 'all') &&
      member.status !== TeamMemberStatus.Locked
    ) {
      // Only return accountants in Locked list if the invitationsLimit === 1
      if (isFreeRolesOnly) {
        return hasRole(member, TeamMemberRole.Accountant);
      }
      return true;
    }
    return (
      hasRole(
        member,
        selectedTab ? (tabToFilter[selectedTab] as TeamMemberRole) : null,
      ) && member.status !== TeamMemberStatus.Locked
    );
  });

  const queryLowerCase = query.toLowerCase();
  const allFilteredMembers = roleFilteredMembers.filter((member) => {
    // If there is no query, we keep every member otherwise we keep only the ones which contains the query in their email/first name/last name
    return (
      !queryLowerCase ||
      member.email?.toLowerCase().includes(queryLowerCase) ||
      member.firstName?.toLowerCase().includes(queryLowerCase) ||
      member.lastName?.toLowerCase().includes(queryLowerCase)
    );
  });

  return allFilteredMembers;
};
